import type { AccessLevel } from '@/interfaces/Access.interface';
import type { BuildingId } from '@/interfaces/Building.interface';
import {
    momentDateToUnzonedDateTime,
    type UnzonedDateTime,
    unzonedDateTimeToApiDateFormat,
    unzonedDateTimeToMomentDate
} from '@/interfaces/Date.interface';
import type { SensorTimeseriesId } from '@/interfaces/Timeseries.interface';
import type { UnitCode } from '@/interfaces/Unit.interface';
import type { APIError } from '@/providers/errors.constants';
import {
    convertPaginatedRequestParamsToString,
    type CoreApiPaginatedRequestParams,
    type CoreApiPaginatedResponse,
    CoreApiVersionPath,
    RosaCoreApiService
} from '@/rosa';
import { restartOn } from '@/utils/rxjs/operators/restart-on.operator';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, type Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
    AlertOccupancyMode,
    type AlertScenarioId,
    type AlertScenarioStatus,
    type AlertScenarioType,
    type AlertStatPeriodCode,
    type AlertTypeCode,
    type AlertValidation
} from '../../alerts.interface';
import type {
    MeasureTypeCode,
    ThresholdAlertScenarioCreation,
    ThresholdAlertScenariosFilters,
    ThresholdAlertScenarioTimeseriesPoint,
    ThresholdAlertScenarioUpdate,
    ThresholdAlertTimeseriesPoint,
    ThresholdScenario,
    ThresholdScenarioGlobalSummary,
    ThresholdScenarioSensorStat,
    ThresholdScenarioStat,
    ThresholdScenarioSummary
} from '../threshold-alert-scenario.interface';

const sortedBackendDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

interface ThresholdScenarioSummaryResponse {
    alertScenarioId: AlertScenarioId;
    scenarioType: AlertScenarioType;
    name: string;
    measureType: MeasureTypeCode;
    access: AccessLevel;
    isActiveInOccupation: boolean;
    isActiveInInoccupation: boolean;
    alertCount: number;
    impactedBuildingCount: number;
    buildingCount: number;
    impactedSensorCount: number;
    sensorCount: number;
    status: AlertScenarioStatus;
    alertProgress: number | null;
    impactedBuildingProgress: number | null;
    impactedSensorProgress: number | null;
}

export interface CoreThresholdScenarioAlertsTimeseriesAnalysis {
    alertTimeseries: ThresholdAlertTimeseriesPoint[];
    alertScenarioThresholdTimeseries: ThresholdAlertScenarioTimeseriesPoint[];
}

@Injectable({
    providedIn: 'root'
})
export class AdvizeoCoreThresholdScenarioService {
    private _rosaCoreApiService = inject(RosaCoreApiService);

    private _alertScenarioSaved$ = new Subject<AlertScenarioId>();

    fetchAlertScenarios$(
        paginatedParams: CoreApiPaginatedRequestParams,
        periodCode: AlertStatPeriodCode,
        viewAllProjectScenarios: boolean,
        filters: ThresholdAlertScenariosFilters
    ): Observable<CoreApiPaginatedResponse<ThresholdScenarioSummary>> {
        interface ThresholdAlertScenarioFiltersDto {
            search: string | null;
            measureType: MeasureTypeCode | null;
            scenarioStatus: AlertScenarioStatus | null;
        }

        const ThresholdScenarioFilters: ThresholdAlertScenarioFiltersDto = {
            search: filters.search,
            measureType: filters.measureType,
            scenarioStatus: filters.status
        };

        const params = convertPaginatedRequestParamsToString(paginatedParams);
        const url = viewAllProjectScenarios ? 'alerts/scenario/threshold/search/all' : 'alerts/scenario/threshold/search';
        return this._rosaCoreApiService
            .post<
                CoreApiPaginatedResponse<ThresholdScenarioSummaryResponse>
            >(`${CoreApiVersionPath.V5}${url}?periodCode=${periodCode}&${params}`, ThresholdScenarioFilters)
            .pipe(
                map(paginatedResponse => {
                    const alertScenarios: ThresholdScenarioSummary[] = paginatedResponse.content.map(alert =>
                        this.mapToAlertScenarioSummary(alert)
                    );

                    return { ...paginatedResponse, content: alertScenarios };
                })
            );
    }

    getAlertScenarioSummary$(alertScenarioId: AlertScenarioId, periodCode: AlertStatPeriodCode): Observable<ThresholdScenarioSummary> {
        return this._rosaCoreApiService
            .get<ThresholdScenarioSummaryResponse>(
                `${CoreApiVersionPath.V5}alerts/scenarios/threshold/${alertScenarioId}/summary?periodCode=${periodCode}`
            )
            .pipe(map(alert => this.mapToAlertScenarioSummary(alert)));
    }

    private mapToAlertScenarioSummary(scenario: ThresholdScenarioSummaryResponse): ThresholdScenarioSummary {
        let occupancy = AlertOccupancyMode.ALL;
        if (scenario.isActiveInInoccupation && !scenario.isActiveInOccupation) {
            occupancy = AlertOccupancyMode.INOCCUPANCY;
        } else if (!scenario.isActiveInInoccupation && scenario.isActiveInOccupation) {
            occupancy = AlertOccupancyMode.OCCUPANCY;
        }
        return {
            alertScenarioId: scenario.alertScenarioId,
            scenarioType: scenario.scenarioType,
            name: scenario.name,
            measureType: scenario.measureType,
            occupancy,
            alertCount: scenario.alertCount,
            impactedBuildingCount: scenario.impactedBuildingCount,
            buildingCount: scenario.buildingCount,
            impactedSensorCount: scenario.impactedSensorCount,
            sensorCount: scenario.sensorCount,
            status: scenario.status,
            access: scenario.access,
            alertProgress: scenario.alertProgress,
            impactedBuildingProgress: scenario.impactedBuildingProgress,
            impactedSensorProgress: scenario.impactedSensorProgress
        };
    }

    getAlertScenarioGlobalSummary$(periodCode: AlertStatPeriodCode): Observable<ThresholdScenarioGlobalSummary> {
        interface RouteResponse {
            alertCount: number;
            impactedBuildingCount: number;
            buildingCount: number;
            impactedSensorCount: number;
            sensorCount: number;
            alertProgress: number | null;
            impactedBuildingProgress: number | null;
            impactedSensorProgress: number | null;
        }
        return this._rosaCoreApiService.get<RouteResponse>(
            `${CoreApiVersionPath.V5}alerts/scenario/threshold/global-stats?periodCode=${periodCode}`
        );
    }

    fetchAlertScenarioBuildingsStats$(
        paginatedParams: CoreApiPaginatedRequestParams,
        alertScenarioId: AlertScenarioId,
        periodCode: AlertStatPeriodCode,
        search: string | null
    ): Observable<CoreApiPaginatedResponse<ThresholdScenarioStat>> {
        interface RouteResponse {
            buildingId: BuildingId;
            sensorCount: number;
            alertCount: number;
        }
        const params = convertPaginatedRequestParamsToString(paginatedParams);
        return this._rosaCoreApiService.post<CoreApiPaginatedResponse<RouteResponse>>(
            `${CoreApiVersionPath.V5}alerts/scenarios/threshold/${alertScenarioId}/buildings-stats/search?periodCode=${periodCode}&${params}`,
            { search }
        );
    }

    fetchAlertScenarioSensorStats$(
        alertScenarioId: AlertScenarioId,
        buildingId: BuildingId,
        startDate: UnzonedDateTime,
        endDate: UnzonedDateTime
    ): Observable<ThresholdScenarioSensorStat[]> {
        return this._rosaCoreApiService.checkedPost(
            `/api/v5/alerts/scenarios/threshold/{alertScenarioId}/buildings/{buildingId}/sensors-stats`,
            {
                path: {
                    alertScenarioId,
                    buildingId
                },
                body: {
                    startDate: unzonedDateTimeToApiDateFormat(startDate),
                    endDate: unzonedDateTimeToApiDateFormat(momentDateToUnzonedDateTime(unzonedDateTimeToMomentDate(endDate).add(1, 'day')))
                }
            }
        );
    }

    getAlertScenario$(alertScenarioId: AlertScenarioId): Observable<ThresholdScenario> {
        interface RouteResponse {
            alertScenarioId: AlertScenarioId;
            scenarioType: AlertScenarioType.THRESHOLD;
            scenarioStatus: AlertScenarioStatus;
            name: string;
            measureType: MeasureTypeCode;
            access: AccessLevel;
            unit: UnitCode;
            thresholdHigh: number;
            thresholdLow: number;
            periodInfos: {
                isActiveInOccupation: boolean;
                isActiveInInoccupation: boolean;
                isActiveDuringSchoolHolidays: boolean;
                isActiveDuringPublicHolidays: boolean;
                periodStart: {
                    day: number;
                    month: number;
                };
                periodEnd: {
                    day: number;
                    month: number;
                };
                daysRanges: Record<
                    string,
                    {
                        isActivated: boolean;
                        ranges: {
                            start: string;
                            end: string;
                        }[];
                    }
                >;
            };
        }

        return this._rosaCoreApiService.get<RouteResponse>(`${CoreApiVersionPath.V5}alerts/scenario/${alertScenarioId}`).pipe(
            restartOn(this._alertScenarioSaved$.pipe(filter(id => id === alertScenarioId))),
            map(s => ({
                alertScenarioId: s.alertScenarioId,
                scenarioType: s.scenarioType,
                status: s.scenarioStatus,
                name: s.name,
                measureType: s.measureType,
                unitCode: s.unit,
                thresholdLow: s.thresholdLow,
                thresholdHigh: s.thresholdHigh,
                access: s.access,
                period: {
                    occupancy:
                        s.periodInfos.isActiveInInoccupation && s.periodInfos.isActiveInOccupation
                            ? AlertOccupancyMode.ALL
                            : s.periodInfos.isActiveInInoccupation
                              ? AlertOccupancyMode.INOCCUPANCY
                              : s.periodInfos.isActiveInOccupation
                                ? AlertOccupancyMode.OCCUPANCY
                                : AlertOccupancyMode.ALL,
                    alertHolidays: s.periodInfos.isActiveDuringSchoolHolidays,
                    alertPublicHolidays: s.periodInfos.isActiveDuringPublicHolidays,
                    periodStart: s.periodInfos.periodStart,
                    periodEnd: s.periodInfos.periodEnd
                },
                weeklyRanges: Object.entries(s.periodInfos.daysRanges || {})
                    .sort(([day1], [day2]) => {
                        return sortedBackendDays.indexOf(day1) - sortedBackendDays.indexOf(day2);
                    })
                    .map(([_, v]) => ({
                        isActivated: v.isActivated,
                        ranges: v.ranges || []
                    }))
            }))
        );
    }

    saveAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return firstValueFrom(
            this._rosaCoreApiService.post<void>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/save`, {})
        ).then(() => {
            this._alertScenarioSaved$.next(alertScenarioId);
        });
    }

    publishAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return firstValueFrom(
            this._rosaCoreApiService.post<void>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/publish`, {})
        ).then(() => {
            this._alertScenarioSaved$.next(alertScenarioId);
        });
    }

    createThresholdAlertScenario(thresholdScenario: ThresholdAlertScenarioCreation): Promise<AlertScenarioId> {
        return firstValueFrom(
            this._rosaCoreApiService.checkedPost('/api/v5/alerts/scenario/threshold', {
                body: {
                    name: thresholdScenario.name,
                    measureType: thresholdScenario.measureType,
                    unit: thresholdScenario.unit
                }
            })
        );
    }

    updateThresholdAlertScenario(alertScenarioId: AlertScenarioId, update: ThresholdAlertScenarioUpdate): Promise<void> {
        interface RouteRequest {
            name?: string;
            thresholdHigh?: number;
            thresholdLow?: number;

            isActiveInOccupation?: boolean;
            isActiveInInoccupation?: boolean;
            isActiveDuringSchoolHolidays?: boolean;
            isActiveDuringPublicHolidays?: boolean;
            periodStart?: {
                day: number;
                month: number;
            };
            periodEnd?: {
                day: number;
                month: number;
            };
            daysRanges?: Record<
                string,
                {
                    isActivated: boolean;
                    ranges: {
                        start: string;
                        end: string;
                    }[];
                }
            >;
        }

        const body: RouteRequest = {
            name: update.name,
            thresholdLow: update.thresholdLow,
            thresholdHigh: update.thresholdHigh,
            isActiveInOccupation: update.period
                ? update.period.occupancy === AlertOccupancyMode.ALL || update.period.occupancy === AlertOccupancyMode.OCCUPANCY
                : undefined,
            isActiveInInoccupation: update.period
                ? update.period.occupancy === AlertOccupancyMode.ALL || update.period.occupancy === AlertOccupancyMode.INOCCUPANCY
                : undefined,
            isActiveDuringSchoolHolidays: update.period ? update.period.alertHolidays : undefined,
            isActiveDuringPublicHolidays: update.period ? update.period.alertPublicHolidays : undefined,
            periodStart: update.period ? update.period.periodStart : undefined,
            periodEnd: update.period ? update.period.periodEnd : undefined,
            daysRanges: update.weeklyRanges
                ? update.weeklyRanges.reduce(
                      (acc, currentValue, index) => {
                          return {
                              ...acc,
                              [sortedBackendDays[index]]: { isActivated: currentValue.isActivated, ranges: currentValue.ranges }
                          };
                      },
                      {} as Record<
                          string,
                          {
                              isActivated: boolean;
                              ranges: {
                                  start: string;
                                  end: string;
                              }[];
                          }
                      >
                  )
                : undefined
        };

        return firstValueFrom(
            this._rosaCoreApiService.put<void>(`${CoreApiVersionPath.V5}alerts/scenario/${alertScenarioId}/threshold`, body)
        );
    }

    alertScenarioValidation$(alertScenarioId: AlertScenarioId): Observable<AlertValidation> {
        interface RouteResponse {
            errors: APIError[];
            valid: boolean;
        }

        return this._rosaCoreApiService
            .get<RouteResponse>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/check`)
            .pipe(restartOn(this._alertScenarioSaved$.pipe(filter(savedScenarioId => savedScenarioId === alertScenarioId))));
    }

    getThresholdScenarioAlertsTimeseriesAnalysis$(
        alertScenarioId: AlertScenarioId,
        timeseriesId: SensorTimeseriesId,
        startDate: UnzonedDateTime,
        endDate: UnzonedDateTime
    ): Observable<CoreThresholdScenarioAlertsTimeseriesAnalysis> {
        interface RouteRequest {
            startDate: string;
            endDate: string;
        }

        interface RouteResponse {
            alertTimeseries: {
                datetime: UnzonedDateTime;
                value: number;
                alertTypeCode: AlertTypeCode;
            }[];
            alertScenarioThresholdTimeseries: {
                lowBound: number;
                highBound: number;
                datetime: UnzonedDateTime;
                activeAfter: boolean;
                inactivityStatuses: string[];
            }[];
        }

        const body: RouteRequest = {
            startDate: unzonedDateTimeToApiDateFormat(startDate),
            endDate: unzonedDateTimeToApiDateFormat(momentDateToUnzonedDateTime(unzonedDateTimeToMomentDate(endDate).add(1, 'day')))
        };

        return this._rosaCoreApiService.post<RouteResponse>(
            `${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/analysis/${timeseriesId}`,
            body
        ); // sensor's unit
    }
}
